import React from "react";
import { motion } from "framer-motion";
import Container from "components/Container";
import CreateAccountModal from "components/CreateAccountModal";
import DownloadModal from "components/DownloadModal";
import { useSignupForm } from "hooks/useSignupForm";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "components/TurnstileWidget";

import styles from "./styles.module.scss";

import LogoWinAZ from "assets/img-winaz/logo.webp";
import TypoWinAZ from "assets/img-winaz/typo.webp";
import BackgroundDesktop from "assets/img-winaz/background/bgwweb2.webp";
import BackgroundMobile from "assets/img-winaz/background/bg.webp";
import BackgroundBottom from "assets/img-winaz/background/bgbottom.webp";
import BackgroundGirl from "assets/img-winaz/background/gai.webp";
import BackgroundMoney from "assets/img-winaz/background/money.webp";
import ButtonRegister from "assets/img-winaz/button/btndawng ki.webp";
import Label29k from "assets/img-winaz/button/label29k.webp";
import ButtonDownload from "assets/img-winaz/button/btntaigame.webp";
import InputBackground from "assets/img-winaz/signup-form/input.webp";
import IconUser from "assets/img-winaz/signup-form/iconusser.webp";
import IconPassword from "assets/img-winaz/signup-form/iconpassword.webp";
import FormBackground from "assets/img-winaz/signup-form/bgpopup.webp";
import SocialFacebook from "assets/img-winaz/social-icon/001-facebook.webp";
import SocialTelegram from "assets/img-winaz/social-icon/002-telegram.webp";
import { getPlayGameDomain } from "utils";
import { SOCIAL_LINKS } from "const";

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

type SocialLink = {
  id: string;
  icon: string;
  alt: string;
  url: string;
  target?: "_self" | "_blank";
};

const SOCIAL_ITEMS: SocialLink[] = [
  {
    id: "facebook",
    icon: SocialFacebook,
    alt: "Facebook",
    url: SOCIAL_LINKS.facebook,
    target: "_blank",
  },
  {
    id: "telegram",
    icon: SocialTelegram,
    alt: "Telegram",
    url: SOCIAL_LINKS.telegram,
    target: "_blank",
  },
];

const INTERCOM_APP_ID = "snx821c4";

type IntercomModule = typeof import("@intercom/messenger-js-sdk");

const INTERACTIVE_IMAGE_PROPS = {
  initial: { filter: "brightness(1)", scale: 1 },
  whileHover: { filter: "brightness(1.08)", scale: 1.03 },
  whileTap: { scale: 0.96 },
  transition: {
    type: "spring" as const,
    stiffness: 450,
    damping: 32,
    mass: 0.28,
  },
};

const SimpleLanding: React.FunctionComponent = () => {
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    React.useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const intercomModuleRef = React.useRef<Promise<IntercomModule | null> | null>(
    null
  );
  const turnstileWidgetRef = React.useRef<TurnstileWidgetHandle | null>(null);
  const hasTrackedInlineFormStartRef = React.useRef(false);

  const handleResetTurnstile = React.useCallback(() => {
    turnstileWidgetRef.current?.reset();
  }, []);

  const pushDataLayerEvent = React.useCallback(
    (eventName: string, payload: Record<string, any> = {}) => {
      if (typeof window === "undefined") {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...payload,
      });
    },
    []
  );

  const trackInlineFormStart = React.useCallback(() => {
    if (hasTrackedInlineFormStartRef.current) {
      return;
    }

    hasTrackedInlineFormStartRef.current = true;
    pushDataLayerEvent("lp_form_start", { form_id: "register_inline" });
  }, [pushDataLayerEvent]);

  const {
    username,
    password,
    apiError,
    usernameError,
    passwordError,
    isSubmitting,
    turnstileRequired,
    handleSubmit: handleInlineSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleTurnstileTokenChange,
  } = useSignupForm({
    requireConfirmPassword: false,
    formId: "register_inline",
    onTurnstileReset: handleResetTurnstile,
  });

  const loadIntercom = React.useCallback(() => {
    if (typeof window === "undefined") {
      return Promise.resolve(null);
    }

    if (!intercomModuleRef.current) {
      intercomModuleRef.current = import("@intercom/messenger-js-sdk")
        .then((module) => {
          module.default({
            app_id: INTERCOM_APP_ID,
          });
          return module;
        })
        .catch((error) => {
          console.error("Failed to load Intercom messenger", error);
          intercomModuleRef.current = null;
          return null;
        });
    }

    return intercomModuleRef.current;
  }, []);

  React.useEffect(() => {
    loadIntercom();
  }, [loadIntercom]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleDownloadClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_button", { button_id: "download" });
    setIsDownloadModalOpen(true);
  }, [pushDataLayerEvent]);


  const handleLogoClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_logo", { location: "header" });
  }, [pushDataLayerEvent]);

  const handleSocialClick = React.useCallback(
    (id: string, url: string, target: "_self" | "_blank", location: string) => {
      pushDataLayerEvent("lp_click_social", { social_id: id, location });
      if (typeof window !== "undefined") {
        window.open(url, target);
      }
    },
    [pushDataLayerEvent]
  );

  return (
    <div
      className={styles.slider}
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.05) 100%), url(${BackgroundDesktop})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }}
    >
      <img
        className={styles.backgroundMobile}
        src={BackgroundMobile}
        alt=""
        aria-hidden="true"
      />
      <img
        className={styles.backgroundBottom}
        src={BackgroundBottom}
        alt=""
        aria-hidden="true"
      />
      <img
        className={styles.backgroundMoney}
        src={BackgroundMoney}
        alt=""
        aria-hidden="true"
      />

      <section id="section-hero" className={`${styles.slide} ${styles.hero}`}>
        <div className={styles.heroFigureWrapper}>
          <img className={styles.heroFigure} src={BackgroundGirl} alt="" />
        </div>
        <Container className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <button
              type="button"
              id="logo"
              className={`${styles.logoButton} ${styles.logoPrimary}`}
              onClick={handleLogoClick}
              aria-label="WinAZ"
            >
              <img src={LogoWinAZ} alt="WinAZ" />
            </button>
            <button
              type="button"
              className={`${styles.logoButton} ${styles.logoTypo}`}
              onClick={handleLogoClick}
              aria-label="WinAZ.com - Đẳng cấp thượng lưu"
            >
              <img src={TypoWinAZ} alt="WinAZ.com - Đẳng cấp thượng lưu" />
            </button>

            <div className={styles.heroFormCluster}>
              <form
                className={styles.loginCard}
                style={{ backgroundImage: `url(${FormBackground})` }}
                onSubmit={handleInlineSubmit}
              >
                <div className={styles.loginFields}>
                  <div className={styles.fieldGroup}>
                    <label
                      className={styles.inputWrapper}
                      style={{ backgroundImage: `url(${InputBackground})` }}
                    >
                      <img src={IconUser} alt="" aria-hidden="true" />
                      <input
                        type="text"
                        placeholder="Tên đăng nhập"
                        value={username}
                        onChange={handleUsernameChange}
                        onFocus={trackInlineFormStart}
                        disabled={isSubmitting}
                      />
                    </label>
                    {usernameError ? (
                      <div className={styles.inlineError} aria-live="assertive">
                        Vui lòng nhập tên đăng nhập
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.fieldGroup}>
                    <label
                      className={styles.inputWrapper}
                      style={{ backgroundImage: `url(${InputBackground})` }}
                    >
                      <img src={IconPassword} alt="" aria-hidden="true" />
                      <input
                        type="password"
                        placeholder="Mật khẩu"
                        value={password}
                        onChange={handlePasswordChange}
                        onFocus={trackInlineFormStart}
                        disabled={isSubmitting}
                      />
                    </label>
                    {passwordError ? (
                      <div className={styles.inlineError} aria-live="assertive">
                        Vui lòng nhập mật khẩu
                      </div>
                    ) : null}
                  </div>
                  {turnstileRequired ? (
                    <div className={styles.turnstileGroup}>
                      <div className={styles.turnstileBlock}>
                        <div className={styles.turnstileInner}>
                          <TurnstileWidget
                            ref={turnstileWidgetRef}
                            className={styles.turnstileWidget}
                            onTokenChange={handleTurnstileTokenChange}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {apiError ? (
                    <div className={styles.inlineError} aria-live="assertive">
                      {apiError}
                    </div>
                  ) : null}
                </div>

                <button
                  type="submit"
                  className={styles.registerButton}
                  disabled={isSubmitting}
                  data-cta
                >
                  <img
                    src={Label29k}
                    alt="29k"
                    className={styles.registerLabel}
                  />
                  <motion.img
                    {...INTERACTIVE_IMAGE_PROPS}
                    src={ButtonRegister}
                    alt="Đăng ký"
                  />
                </button>
              </form>

              <div className={styles.heroActions}>
                <button
                  type="button"
                  className={`${styles.heroActionButton} ${styles.heroDownloadButton}`}
                  id="btn-download"
                  onClick={handleDownloadClick}
                >
                  <motion.img
                    {...INTERACTIVE_IMAGE_PROPS}
                    src={ButtonDownload}
                    alt="Tải game"
                  />
                </button>
              </div>

              <div className={styles.heroSocials}>
                {SOCIAL_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    id={`social-${item.id}`}
                    type="button"
                    className={`social_platform_${item.id}`}
                    onClick={() =>
                      handleSocialClick(
                        item.id,
                        item.url,
                        item.target ?? "_self",
                        "hero"
                      )
                    }
                  >
                    <img src={item.icon} alt={item.alt} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      <CreateAccountModal
        isOpen={isCreateAccountModalOpen}
        onClose={() => setIsCreateAccountModalOpen(false)}
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
      />
    </div>
  );
};

export default SimpleLanding;
